import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { ErrorState, Loading } from '../components/States.js';
import { Dd, MultiDd } from '../components/Dropdown.js';
import { DateField } from '../components/DateTimeField.js';
import { timeOpts } from '../lib/timePrefs.js';
import { ROLE_LABEL } from './usersShared.js';
import {
  actionLabel,
  auditQueryString,
  categoryLabel,
  messagingLink,
  messagingWhere,
  type AuditFilters,
  type MessagingDetail,
} from '../lib/auditLog.js';
import {
  fetchMessageTexts,
  messageKey,
  messageRef,
  messageRefs,
  messageTextView,
  type MessageText,
  type MessageTextView,
} from '../lib/auditMessageText.js';

// ── Audit Log — who opened, viewed or changed what, and when (ehr#61, ehr#345) ──

interface AuditRow {
  id: string;
  ts: string;
  actorId: string | null;
  actor: string;
  actorRole: string | null;
  action: string;
  kind: 'view' | 'change';
  entity: string;
  entityId: string | null;
  clientId: string | null;
  client: string | null;
  /** messaging#48/#58: which conversation (Messaging's entries only) */
  messaging?: MessagingDetail | null;
}
interface AuditResp {
  rows: AuditRow[];
  total: number;
  filtered: number;
  page: number;
  pageSize: number;
  users: { id: string; name: string; role: string }[];
  categories: string[];
}
const when = (s: string) =>
  new Date(s).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...timeOpts(),
  });
const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');
const roleLabel = (r: string | null) =>
  r ? (ROLE_LABEL[r as keyof typeof ROLE_LABEL] ?? titleCase(r)) : '';

const DATE_WINDOWS: { value: string; label: string; days: number }[] = [
  { value: 'all', label: 'All time', days: 0 },
  { value: '1', label: 'Today', days: 1 },
  { value: '7', label: 'Last 7 days', days: 7 },
  { value: '30', label: 'Last 30 days', days: 30 },
  { value: '90', label: 'Last 90 days', days: 90 },
  { value: 'custom', label: 'Date range…', days: 0 },
];
const KINDS = [
  { value: '', label: 'Viewed and changed' },
  { value: 'view', label: 'Viewed / opened' },
  { value: 'change', label: 'Changed' },
];
const PAGE_SIZE = 100;
const isoDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * messaging#58 (10-21, option b): what the message said, read live from Messaging (never stored,
 * never in the CSV). Long text shows three lines, with Show more.
 */
function AuditMessageText({ view }: { view: MessageTextView | null }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !open) setLong(el.scrollHeight > el.clientHeight + 1);
  }, [view?.body, open]);
  if (!view) return null;
  return (
    <span className={view.muted ? 'audit-msg audit-msg-muted' : 'audit-msg'}>
      <span ref={ref} className={open ? 'audit-msg-text' : 'audit-msg-text audit-msg-clamp'}>
        {view.body}
      </span>
      {(view.edited || long || open) && (
        <span className="audit-msg-foot">
          {view.edited && <span>(edited)</span>}
          {(long || open) && (
            <button type="button" className="audit-msg-more" onClick={() => setOpen(!open)}>
              {open ? 'Show less' : 'Show more'}
            </button>
          )}
        </span>
      )}
    </span>
  );
}

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** ehr#61: `embedded` = inside Data & Settings → Audit log, which has its own heading. */
export function AuditView({ embedded = false }: { embedded?: boolean } = {}) {
  const [search, setSearch] = useState('');
  const [q, setQ] = useState(''); // search, settled
  const [userF, setUserF] = useState<string[]>([]);
  const [roleF, setRoleF] = useState<string[]>([]);
  const [catF, setCatF] = useState<string[]>([]);
  const [kindF, setKindF] = useState('');
  const [dateF, setDateF] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [pageAt, setPageAt] = useState({ key: '', page: 1 });
  const [data, setData] = useState<AuditResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  // messaging#58: message text by messageKey (null = it could not be read); asked = requested once
  const [texts, setTexts] = useState<ReadonlyMap<string, MessageText | null>>(new Map());
  const [textDenied, setTextDenied] = useState(false);
  const asked = useRef(new Set<string>());

  // Type, then search: one request once typing pauses.
  useEffect(() => {
    const t = window.setTimeout(() => setQ(search.trim()), 350);
    return () => window.clearTimeout(t);
  }, [search]);

  const filters: AuditFilters = useMemo(() => {
    let f = '';
    let t = '';
    if (dateF === 'custom') {
      f = from;
      t = to;
    } else {
      const w = DATE_WINDOWS.find((d) => d.value === dateF);
      if (w && w.days) {
        const d = new Date();
        d.setDate(d.getDate() - (w.days - 1));
        f = isoDay(d);
      }
    }
    return {
      q,
      actors: userF,
      roles: roleF,
      categories: catF,
      kind: kindF as AuditFilters['kind'],
      from: f,
      to: t,
    };
  }, [q, userF, roleF, catF, kindF, dateF, from, to]);
  const filterKey = auditQueryString(filters);
  // The page belongs to the filters it was picked under: a filter change is page 1 again.
  const page = pageAt.key === filterKey ? pageAt.page : 1;
  const setPage = (p: number) => setPageAt({ key: filterKey, page: p });

  // The filters stay on screen while a page loads (the rows already shown stay too).
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get<AuditResp>(`/audit?page=${page}&pageSize=${PAGE_SIZE}&${filterKey}`)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setError('');
      })
      .catch((e: unknown) => alive && setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [filterKey, page]);

  // messaging#58: the text of the page's messages, in one request; Messaging refuses (403) anyone
  // who may not read it, and then nothing extra shows.
  useEffect(() => {
    if (!data || textDenied) return;
    const want = messageRefs(data.rows).filter((m) => !asked.current.has(messageKey(m)));
    if (!want.length) return;
    want.forEach((m) => asked.current.add(messageKey(m)));
    void fetchMessageTexts(want).then((got) => {
      if (got === 'denied') {
        setTextDenied(true);
        return;
      }
      setTexts((prev) => {
        const next = new Map(prev);
        for (const m of want) next.set(messageKey(m), got?.get(messageKey(m)) ?? null);
        return next;
      });
    });
  }, [data, textDenied]);
  // the line under a message's entry, once its text is in
  const textViewOf = (r: AuditRow): MessageTextView | null => {
    const m = messageRef(r.messaging);
    if (!m || textDenied || !texts.has(messageKey(m))) return null;
    return messageTextView(texts.get(messageKey(m)) ?? null, r.action);
  };

  const userOpts = useMemo(
    () =>
      (data?.users ?? []).map((u) => ({
        value: u.id,
        label: u.role ? `${u.name} (${roleLabel(u.role)})` : u.name,
      })),
    [data?.users],
  );
  const roleOpts = Object.entries(ROLE_LABEL).map(([value, label]) => ({ value, label }));
  const catOpts = (data?.categories ?? []).map((c) => ({ value: c, label: categoryLabel(c) }));

  // Every entry the filters find, not just this page (up to 20,000).
  const exportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const all: AuditRow[] = [];
      const pages = Math.min(Math.ceil(data.filtered / 1000), 20);
      for (let p = 1; p <= pages; p++) {
        const d = await api.get<AuditResp>(`/audit?page=${p}&pageSize=1000&${filterKey}`);
        all.push(...d.rows);
      }
      const header = [
        'Time',
        'User',
        'Level',
        'Action',
        'Code',
        'Type',
        'Client',
        'Conversation',
        'Record',
        'Record ID',
      ];
      const lines = [
        header.join(','),
        ...all.map((r) =>
          [
            new Date(r.ts).toISOString(),
            r.actor,
            roleLabel(r.actorRole),
            actionLabel(r.action),
            r.action,
            r.kind === 'view' ? 'Viewed' : 'Changed',
            r.client ?? '',
            messagingWhere(r.messaging),
            r.entity,
            r.entityId ?? '',
          ]
            .map((c) => csvCell(String(c)))
            .join(','),
        ),
      ];
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${isoDay(new Date())}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not export the log.');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.filtered / PAGE_SIZE)) : 1;
  const cols = '160px 1.1fr 1.5fr 1fr 96px';

  return (
    <div className={embedded ? 'admin-view admin-view-embedded' : 'admin-view'}>
      {!embedded && (
        <div className="admin-head">
          <h1 className="admin-title">Audit Log</h1>
          <p className="muted">
            Every chart, form and record opened, viewed or changed — who, what, which client and
            when.
          </p>
        </div>
      )}
      <div className="card panel">
        <div className="audit-toolbar">
          <span className="muted">
            {data ? (
              <>
                Showing <strong>{data.filtered.toLocaleString()}</strong>
                {data.filtered !== data.total ? ` of ${data.total.toLocaleString()}` : ''} events
              </>
            ) : (
              'Loading…'
            )}
          </span>
          <button
            className="btn sm"
            onClick={() => void exportCsv()}
            disabled={!data || data.filtered === 0 || exporting}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
        <div className="filter-bar">
          <input
            type="text"
            placeholder="Search a person, a client, an action or a conversation…"
            aria-label="Search the log"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <MultiDd
            values={userF}
            onChange={setUserF}
            placeholder="All users"
            ariaLabel="Filter by users"
            selectAll
            options={[{ value: 'system', label: 'System' }, ...userOpts]}
          />
          <MultiDd
            values={roleF}
            onChange={setRoleF}
            placeholder="All levels"
            ariaLabel="Filter by user level"
            searchable={false}
            selectAll
            options={roleOpts}
          />
          <MultiDd
            values={catF}
            onChange={setCatF}
            placeholder="All actions"
            ariaLabel="Filter by actions"
            selectAll
            options={catOpts}
          />
          <Dd
            value={kindF}
            onChange={setKindF}
            searchable={false}
            ariaLabel="Viewed or changed"
            options={KINDS}
          />
          <Dd
            value={dateF}
            onChange={setDateF}
            searchable={false}
            ariaLabel="Filter by date"
            options={DATE_WINDOWS.map((d) => ({ value: d.value, label: d.label }))}
          />
          {dateF === 'custom' && (
            <div className="audit-dates">
              <DateField value={from} onChange={setFrom} placeholder="From" />
              <span className="muted">to</span>
              <DateField value={to} onChange={setTo} min={from || undefined} placeholder="To" />
            </div>
          )}
        </div>
        <div className="list-row head" style={{ gridTemplateColumns: cols }}>
          <span className="list-cell">Time</span>
          <span className="list-cell">User</span>
          <span className="list-cell">Action</span>
          <span className="list-cell">Client</span>
          <span className="list-cell">Type</span>
        </div>
        {error ? (
          <ErrorState message={error} />
        ) : !data ? (
          <Loading />
        ) : data.rows.length === 0 ? (
          <div className="list-row" style={{ cursor: 'default' }}>
            <span className="list-cell muted">
              {data.total === 0 ? 'No audit entries yet.' : 'No events match the current filters.'}
            </span>
          </div>
        ) : (
          <div className={loading ? 'audit-rows audit-loading' : 'audit-rows'} aria-busy={loading}>
            {data.rows.map((r) => (
              <div
                key={r.id}
                className="list-row"
                style={{ gridTemplateColumns: cols, cursor: 'default' }}
              >
                <span className="list-cell muted">{when(r.ts)}</span>
                <span className="list-cell audit-cell">
                  {r.actor}
                  {r.actorRole && <span className="audit-sub">{roleLabel(r.actorRole)}</span>}
                </span>
                <span className="list-cell audit-cell">
                  {actionLabel(r.action)}
                  {messagingWhere(r.messaging) && (
                    <span className="audit-sub audit-where">
                      {r.messaging?.tab ? '' : 'in '}
                      {messagingWhere(r.messaging)}
                      {messagingLink(r.messaging) && (
                        <>
                          {' · '}
                          <a
                            className="audit-open"
                            href={messagingLink(r.messaging)!}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {r.messaging?.eventId ? 'Open message' : 'Open conversation'}
                          </a>
                        </>
                      )}
                    </span>
                  )}
                  <AuditMessageText view={textViewOf(r)} />
                  <span className="audit-sub audit-code">{r.action}</span>
                </span>
                <span className="list-cell">{r.client ?? <span className="muted">—</span>}</span>
                <span className="list-cell">
                  <span className={`audit-kind audit-kind-${r.kind}`}>
                    {r.kind === 'view' ? 'Viewed' : 'Changed'}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
        {data && totalPages > 1 && (
          <div className="audit-pager">
            <span className="muted">
              Page {page} of {totalPages.toLocaleString()}
            </span>
            <div className="audit-pager-btns">
              <button
                className="btn sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage(page - 1)}
              >
                ‹ Prev
              </button>
              <button
                className="btn sm"
                disabled={page >= totalPages || loading}
                onClick={() => setPage(page + 1)}
              >
                Next ›
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
